import { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getGetYardsQueryKey,
  useGetLatestYard,
  useGetYards,
  useUploadYardCsv,
} from '@/api/generated';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatDateForDisplay } from '@/lib/utils';
import CsvUploadButton from '@/routes/(app)/(scenarios)/(create)/components/csv-upload-button';
import SourceOptionList from '@/routes/(app)/(scenarios)/(create)/components/source-option-list';
import { CreateScenarioSchema } from '@/routes/(app)/(scenarios)/(create)/types';

const YardStep: FC = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetYards({ skip: 0, limit: 15 });
  const yards = useMemo(() => data?.items ?? [], [data]);

  const { data: latestYard } = useGetLatestYard();

  const queryClient = useQueryClient();
  const { mutateAsync: uploadYardCsv, isPending: isUploadingYardCsv } =
    useUploadYardCsv();

  const form = useFormContext<CreateScenarioSchema>();
  const { yardId } = form.watch();

  // The latest yard is the usual starting point, so it is pre-selected.
  useEffect(() => {
    if (yardId || !latestYard?.id) return;
    form.setValue('yardId', latestYard.id);
  }, [yardId, latestYard?.id, form]);

  const options = useMemo(
    () =>
      yards.map((yard) => ({
        value: yard.id,
        title: yard.filename,
        description: t('scenarios.create.uploadedAt', {
          date: formatDateForDisplay(yard.upload_timestamp),
        }),
        isLatest: yard.id === latestYard?.id,
      })),
    [yards, latestYard?.id, t]
  );

  const handleUploadYardCsv = useCallback(
    async (file: File) => {
      if (isUploadingYardCsv) return;

      await uploadYardCsv(
        { data: { file } },
        {
          onSuccess: async (response) => {
            await queryClient.invalidateQueries({
              queryKey: getGetYardsQueryKey(),
            });
            form.setValue('yardId', response.id);
          },
          onError: () => {
            toast.error(t('general.toast.somethingWentWrong'));
          },
        }
      );
    },
    [uploadYardCsv, form, t, isUploadingYardCsv, queryClient]
  );

  return (
    <Card>
      <CardHeader className='flex flex-row items-start justify-between gap-3'>
        <div className='flex flex-col gap-1.5'>
          <CardTitle>{t('scenarios.create.uploadYard.title')}</CardTitle>
          <CardDescription>
            {t('scenarios.create.uploadYard.description')}
          </CardDescription>
        </div>
        <CsvUploadButton
          onFile={handleUploadYardCsv}
          isUploading={isUploadingYardCsv}
        />
      </CardHeader>
      <CardContent>
        <SourceOptionList
          label={t('scenarios.create.uploadYard.title')}
          options={options}
          value={yardId}
          onValueChange={(value) => {
            form.setValue('yardId', value);
          }}
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
};

export default YardStep;
